import PropTypes from 'prop-types';
import React from 'react';
import { findDOMNode } from 'react-dom';
import CheckboxGroupInput from './CheckboxGroupInput';
import DebouncedInputField from './DebouncedInputField';
import SearchActionCreators from 'search/actions/SearchActionCreators';
import hardMaxInCapacityRange from 'search/utils/capacityRangeMaxValueHelper';
import styles from './SearchFilters.scss';

const MIN_ALLOWED_CAPACITY = 1;
const MAX_ALLOWED_CAPACITY = 10000;

class SearchFilters extends React.Component {
  static propTypes = {
    params: PropTypes.shape({
      et: PropTypes.number,
      people: PropTypes.string,
      people_filter: PropTypes.string,
      location: PropTypes.string,
      vs: PropTypes.array,
      title: PropTypes.title
    }).isRequired,
    eventTypes: PropTypes.array.isRequired,
    venueServices: PropTypes.array
  };

  render() {
    return (
      <div className="m-search-filters">
        <div className="m-search-filters-main-section">
          <div className="m-search-filters-main-label">
            <label htmlFor="eventTypes">
              <i className="fa fa-tags m-search-filters-main-icon" />
              {__('search.search_filters.event_types.label')}
            </label>
          </div>
          <div className="m-search-filters-main-large">
            <select
              id="eventTypes"
              ref="eventTypeSelect"
              value={this.props.params.et}
              onChange={this._onEventTypeChange}
            >
              <option value="">
                {__('search.search_filters.event_types.placeholder')}
              </option>
              {this.props.eventTypes.map(eventType => {
                return (
                  <option
                    value={eventType.id}
                    key={`event-type:${eventType.id}`}
                  >
                    {eventType.name}
                  </option>
                );
              })}
            </select>
          </div>
        </div>
        <div className={styles.searchFiltersMainCapacitySection}>
          <div className="m-search-filters-main-label">
            <label htmlFor="people">
              <i className="fa fa-home m-search-filters-main-icon" />
              {__('search.search_filters.people.label')}
            </label>
          </div>
          <div className={styles.searchFiltersCapacityFieldsContainer}>
            <div className={styles.searchFiltersCapacityOptions}>
              <div>
                <DebouncedInputField
                  className={styles.searchFiltersCapacityField}
                  type="number"
                  id="people"
                  ref="peopleSelect"
                  min={MIN_ALLOWED_CAPACITY.toString()}
                  max={MAX_ALLOWED_CAPACITY.toString()}
                  value={this._peopleValue()}
                  onChange={this._onPeopleChange}
                  placeholder={__('search.search_filters.people.placeholder')}
                />
              </div>
            </div>
            <div className={styles.searchFiltersCapacityOptions}>
              <select
                className={styles.searchFiltersCapacityField}
                data-testid="search-people-filter-select"
                ref="peopleFilterSelect"
                value={this.props.params.people_filter}
                onChange={this._onPeopleFilterChange}
              >
                <option value="">
                  {__('search.search_filters.people_filter.sitting')}
                </option>
                <option value="standing">
                  {__('search.search_filters.people_filter.standing')}
                </option>
              </select>
            </div>
            <PeopleSectionCapacityInfoText people={this.props.params.people} />
          </div>
        </div>
        <div className="m-search-filters-main-section">
          <div className="m-search-filters-main-label">
            <label htmlFor="location">
              <i className="fa fa-map-marker m-search-filters-main-icon" />
              {__('search.search_filters.location.label')}
            </label>
          </div>
          <div className="m-search-filters-main-large">
            <DebouncedInputField
              type="text"
              id="location"
              ref="locationInput"
              placeholder={__('search.search_filters.location.placeholder')}
              value={this.props.params.location}
              onChange={this._onLocationChange}
            />
          </div>
        </div>
        <div className="m-search-filters-main-section">
          <div className="m-search-filters-main-label">
            <label>
              <i className="fa fa-reorder m-search-filters-main-icon" />
              {__('search.search_filters.venue_services.label')}
            </label>
          </div>
          <div
            className="m-search-filters-main-large m-checkbox-list m-checkbox-list-small"
            data-testid="venue-service-criteria-area"
          >
            <CheckboxGroupInput
              values={this.props.venueServices}
              selectedValues={this.props.params.vs}
              onChange={this._onVenueServicesChange}
              ref="venueServicesCheckboxGroup"
            />
          </div>
        </div>
        <div className="m-search-filters-main-section">
          <div className="m-search-filters-main-label">
            <label htmlFor="title">
              <i className="fa fa-search m-search-filters-main-icon" />
              {__('search.search_filters.title.label')}
            </label>
          </div>
          <div className="m-search-filters-main-large">
            <DebouncedInputField
              type="text"
              id="title"
              ref="titleInput"
              placeholder={__('search.search_filters.title.placeholder')}
              value={this.props.params.title}
              onChange={this._onTitleChange}
            />
          </div>
        </div>
      </div>
    );
  }

  _onTitleChange = () => {
    const title = findDOMNode(this.refs.titleInput).value;
    SearchActionCreators.changeVenueTitleFilter(title);
  };

  _onVenueServicesChange = () => {
    const venueServices = this.refs.venueServicesCheckboxGroup.getSelectedValues();
    SearchActionCreators.changeVenueServicesFilter(venueServices);
  };

  _onEventTypeChange = () => {
    const selectedEventType = findDOMNode(this.refs.eventTypeSelect).value;
    SearchActionCreators.changeEventTypeFilter(selectedEventType);
  };

  _onPeopleChange = () => {
    const peopleAmount = findDOMNode(this.refs.peopleSelect).value;
    if (peopleAmountValid(peopleAmount)) {
      SearchActionCreators.changePeopleFilter(
        this._capacityRange(peopleAmount)
      );
    }
  };

  _capacityRange = people => {
    if (people) {
      const maxValue = hardMaxInCapacityRange(people);
      return `${people}..${maxValue}`;
    } else {
      return people;
    }
  };

  _peopleValue = () => {
    const { people = '' } = this.props.params;
    const hardMinForCapacity = firstNumberFromCapacityRange(people);
    if (hardMinForCapacity) {
      return hardMinForCapacity;
    }
    return '';
  };

  _onPeopleFilterChange = () => {
    const peopleFilter = findDOMNode(this.refs.peopleFilterSelect).value;
    // TODO: Come up with a better name #peoplefilter
    SearchActionCreators.changePeopleFilterFilter(peopleFilter);
  };

  _onLocationChange = () => {
    const location = findDOMNode(this.refs.locationInput).value;
    SearchActionCreators.changeLocationFilter(location);
  };
}

function peopleAmountValid(peopleAmount) {
  return (
    (peopleAmount < MAX_ALLOWED_CAPACITY &&
      peopleAmount >= MIN_ALLOWED_CAPACITY) ||
    peopleAmount === ''
  );
}

function firstNumberFromCapacityRange(people) {
  if (people) {
    const firstNumbersRegex = /^\d+/;
    const firstNumberFromRegex = people.match(firstNumbersRegex);
    if (firstNumberFromRegex) {
      return parseInt(firstNumberFromRegex[0], 10);
    }
  }
  return '';
}

const capacityRangeValues = ({ people = '' }) => {
  if (people) {
    const hardMinForCapacity = firstNumberFromCapacityRange(people);
    if (
      hardMinForCapacity >= MIN_ALLOWED_CAPACITY &&
      hardMinForCapacity <= MAX_ALLOWED_CAPACITY
    ) {
      return {
        min: parseInt(hardMinForCapacity, 10).toString(),
        max: hardMaxInCapacityRange(hardMinForCapacity).toString()
      };
    }
  }
  return {
    min: MIN_ALLOWED_CAPACITY.toString(),
    max: `${MAX_ALLOWED_CAPACITY}+`
  };
};

function PeopleSectionCapacityInfoText(people) {
  const capacityRangeObject = capacityRangeValues(people);
  return (
    <div className={styles.infoTextContainer}>
      <p
        data-testid="capacity-info-text"
        className={styles.infoText}
        dangerouslySetInnerHTML={{
          __html: I18n.t('search.search_filters.people.info', {
            fromNumber: capacityRangeObject['min'],
            toNumber: capacityRangeObject['max']
          })
        }}
      />
    </div>
  );
}

export default SearchFilters;
